import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { getCookie } from 'functions/getCookie';
import { getConfig } from 'functions/getConfig';
import { useQueryParams } from 'hooks/useQueryParams';
import { useLanguageVersions } from 'hooks/useConfig';
import { useLoadFreecodeTakeExercise } from 'hooks/useFreecode';
import { FreecodeLoading } from 'components/FreecodeLoading/FreecodeLoading';
import FreecodePage from 'components/FreecodePage/FreecodePage';
import { useFreecodeContext } from 'hooks/useContext';
import FreecodeEditorComponents from 'components/FreecodePage/FreecodeEditorComponents';
import PageUnderConstruction from 'components/FreecodePage/PageUnderConstruction';
import { identifiers } from 'components/FreecodePage/FreecodeLayoutConstants';
import FreecodeDiscussion from 'components/FreecodeContent/FreecodeDiscussion/FreecodeDiscussion';
import FreecodeCourse from 'components/FreecodeContent/FreecodeCourse/FreecodeCourse';

const HEADER_LINKS = [{ link: '/exercises', label: 'All Exercises' }];

export default function FreecodeExercise() {
  const { exerciseId } = useParams();
  const { courseId } = useQueryParams();

  const [loadingDone, setLoadingDone] = useState(false);
  const [loadingError, setLoadingError] = useState(undefined);
  const [headerLinks, setHeaderLinks] = useState(HEADER_LINKS);
  const [exerciseComponents, setExerciseComponents] = useState({});

  const { setTest, setTestImpl, setLanguageData, setLanguage, setCode, setSponsor, setExerciseOptions, setInput } =
    useFreecodeContext();
  const courseRef = useRef(undefined);

  const [languageVersions /** ignore next */, , languageVersionsError] = useLanguageVersions();
  const [freecodeResponse /** ignore next */, , freecodeError] = useLoadFreecodeTakeExercise(exerciseId, courseId);

  useEffect(() => {
    if (!exerciseId) return;
    setHeaderLinks([
      ...HEADER_LINKS,
      {
        link: `${getConfig().REDIRECTION_ROOT_URL}/exercise/${exerciseId}`,
        label: 'Old Editor',
      },
    ]);
    setExerciseComponents((e) => ({
      ...e,
      [identifiers.discussion]: <FreecodeDiscussion testId={exerciseId} />,
    }));
  }, [exerciseId]);

  useEffect(() => {
    if (!languageVersions || !freecodeResponse) return;
    // When loading is done destructure the data
    try {
      const { test, testImpl, inputExercise, course, sponsor } = freecodeResponse;
      if (course) {
        setExerciseComponents((e) => ({
          ...e,
          [identifiers.course]: <FreecodeCourse course={course} />,
        }));
      }

      let allowedLanguages = languageVersions[testImpl.executionVersion];

      const foundLanguage =
        inputExercise?.lastAutoSave?.language ||
        localStorage.getItem(`fsEditor-exercise-${test._id}-language`) ||
        (allowedLanguages['python'] ? 'python' : Object.keys(allowedLanguages)[0]);

      const foundCode =
        inputExercise?.lastAutoSave?.code ||
        localStorage.getItem(`fsEditor-exercise-${test._id}-${foundLanguage}`) ||
        testImpl.generated?.skeletons?.[foundLanguage] ||
        'Code not found...';
      localStorage.setItem(`fsEditor-exercise-${test._id}-${foundLanguage}`, foundCode);

      // Find level to run
      let levelToRun = 1;
      if (inputExercise?.lastAutoSave) levelToRun = inputExercise?.lastAutoSave?.levelToRun + 1;
      else if (localStorage.getItem(`fsEditor-exercise-${test._id}-levelToRun`))
        levelToRun = JSON.parse(localStorage.getItem(`fsEditor-exercise-${test._id}-levelToRun`));

      // Find how many levels are unlocked
      let unlockedLevels =
        Object.keys(inputExercise?.levelData ?? {}).length ||
        JSON.parse(localStorage.getItem(`fsEditor-exercise-${test._id}-unlockedLevels`)) ||
        1;
      if (
        unlockedLevels < testImpl.configLevels.numPublic &&
        Object.keys(inputExercise?.levelData?.[`${unlockedLevels - 1}`] ?? []).some(
          (languageKey) => inputExercise?.levelData?.[`${unlockedLevels - 1}`]?.[languageKey]?.firstSuccess
        )
      ) {
        // If last level has firstSuccess, then increase unlockedLevels by one
        unlockedLevels++;
      }

      setInput(inputExercise);
      setTest(test);
      setTestImpl(testImpl);
      setLanguageData(allowedLanguages);
      setLanguage(foundLanguage);
      setCode(foundCode);
      if (sponsor && !getCookie('isTicketUser')) setSponsor(sponsor);
      setExerciseOptions({
        levelToRun,
        unlockedLevels,
        nextAvailable: unlockedLevels > levelToRun,
      });
      courseRef.value = course;
      setLoadingDone(true);
    } catch (error) {
      setLoadingError('Could not unpack freecode data');
    }
  }, [
    languageVersions,
    freecodeResponse,
    setLanguageData,
    setLanguage,
    setCode,
    setInput,
    setTest,
    setTestImpl,
    setSponsor,
    setExerciseOptions,
  ]);

  return (
    <FreecodePage headerLinks={headerLinks}>
      <FreecodeLoading
        loading={!loadingDone}
        errors={[languageVersionsError, freecodeError, loadingError]}
        errorLinkText="All Exercises"
        errorLink="/exercises"
      />
      <PageUnderConstruction />
      {loadingDone && <FreecodeEditorComponents parentComponents={exerciseComponents} />}
    </FreecodePage>
  );
}
